import React, { useState, useEffect } from 'react';
import { 
  Sun, 
  Moon, 
  CheckCircle, 
  Clock, 
  Flame, 
  Compass, 
  ChevronRight,
  Activity,
  Coffee,
  BookOpen,
  Utensils,
  Laptop,
  WindArrowDown,
  MoonStar,
  Smile,
  Frown,
  Meh,
  Calendar,
  BarChart2,
  Award,
  Plus
} from 'lucide-react';

const DailyJourney = ({ darkMode }) => {
  // Theme configuration
  const currentTheme = darkMode ? {
    bg: 'bg-gradient-to-br from-gray-900 to-blue-900',
    card: 'bg-gray-800/70 backdrop-blur-md border border-gray-700/50',
    text: 'text-white',
    textSecondary: 'text-gray-400',
    accent: 'text-blue-400',
    divider: 'border-gray-700'
  } : {
    bg: 'bg-gradient-to-br from-blue-50 to-white',
    card: 'bg-white/90 backdrop-blur-md border border-gray-200/60',
    text: 'text-gray-900',
    textSecondary: 'text-gray-600',
    accent: 'text-blue-600',
    divider: 'border-gray-200'
  };

  // Time-based state
  const [currentTime, setCurrentTime] = useState(new Date());
  const [activeTab, setActiveTab] = useState('today');
  const [mood, setMood] = useState(null);
  const [expandedActivity, setExpandedActivity] = useState(null);

  // Update time every minute
  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 60000);
    return () => clearInterval(timer);
  }, []);

  // Time of day detection
  const getTimeOfDay = () => {
    const hours = currentTime.getHours();
    if (hours >= 5 && hours < 12) return 'morning';
    if (hours >= 12 && hours < 17) return 'afternoon';
    if (hours >= 17 && hours < 21) return 'evening';
    return 'night';
  };

  // Sample data
  const timeBasedSuggestions = {
    morning: [
      { id: 1, activity: 'Morning Meditation', icon: <WindArrowDown size={20} />, duration: '15 min', category: 'mindfulness' },
      { id: 2, activity: 'Workout Session', icon: <Activity size={20} />, duration: '30 min', category: 'fitness' },
      { id: 3, activity: 'Healthy Breakfast', icon: <Utensils size={20} />, duration: '20 min', category: 'nutrition' }
    ],
    afternoon: [
      { id: 4, activity: 'Focused Work Block', icon: <Laptop size={20} />, duration: '90 min', category: 'productivity' },
      { id: 5, activity: 'Lunch Break', icon: <Utensils size={20} />, duration: '30 min', category: 'nutrition' },
      { id: 6, activity: 'Short Walk', icon: <Compass size={20} />, duration: '15 min', category: 'fitness' }
    ],
    evening: [
      { id: 7, activity: 'Learn Something New', icon: <BookOpen size={20} />, duration: '45 min', category: 'learning' },
      { id: 8, activity: 'Dinner', icon: <Utensils size={20} />, duration: '30 min', category: 'nutrition' },
      { id: 9, activity: 'Wind Down Routine', icon: <MoonStar size={20} />, duration: '60 min', category: 'mindfulness' }
    ],
    night: [
      { id: 10, activity: 'Reflect on the Day', icon: <Moon size={20} />, duration: '20 min', category: 'mindfulness' },
      { id: 11, activity: 'Prepare for Tomorrow', icon: <CheckCircle size={20} />, duration: '15 min', category: 'productivity' },
      { id: 12, activity: 'Reading Time', icon: <BookOpen size={20} />, duration: '30 min', category: 'learning' }
    ]
  };

  const userRoutines = [
    { id: 101, activity: 'Morning Jog', completed: true, time: '7:00 AM', streak: 5, icon: <Activity size={20} /> },
    { id: 102, activity: 'Read 10 Pages', completed: false, time: '8:30 AM', streak: 12, icon: <BookOpen size={20} /> },
    { id: 103, activity: 'Work Project', completed: false, time: '10:00 AM', streak: 3, icon: <Laptop size={20} /> },
    { id: 104, activity: 'Lunch with Team', completed: false, time: '12:30 PM', streak: 0, icon: <Utensils size={20} /> }
  ];

  const pastActivities = [
    { date: 'Yesterday', completion: '92%', activities: [
      { id: 201, activity: 'Yoga Session', completed: true, duration: '30 min' },
      { id: 202, activity: 'Client Meeting', completed: true, duration: '60 min' },
      { id: 203, activity: 'Spanish Lesson', completed: false, duration: '45 min' }
    ]},
    { date: 'Monday', completion: '85%', activities: [
      { id: 204, activity: 'Gym Workout', completed: true, duration: '60 min' },
      { id: 205, activity: 'Project Deadline', completed: true, duration: '120 min' },
      { id: 206, activity: 'Meditation', completed: false, duration: '15 min' }
    ]}
  ];

  // Calculate completion percentage
  const completionPercentage = Math.round(
    (userRoutines.filter(a => a.completed).length / userRoutines.length) * 100
  ) || 0;

  // Mood selection handler
  const handleMoodSelect = (selectedMood) => {
    setMood(selectedMood);
    // In a real app, you would save this to a database
    console.log(`Mood set to: ${selectedMood}`);
  };

  // Toggle activity completion
  const toggleActivityCompletion = (id) => {
    // In a real app, you would update this in your state management
    console.log(`Toggled activity ${id}`);
  };

  // Time-based greeting
  const getGreeting = () => {
    const timeOfDay = getTimeOfDay();
    const greetings = {
      morning: 'Good Morning',
      afternoon: 'Good Afternoon',
      evening: 'Good Evening',
      night: 'Good Night'
    };
    return greetings[timeOfDay];
  };

  return (
    <div className={`min-h-screen ${currentTheme.bg} p-4 md:p-8 transition-colors duration-300`}>
      <div className="mx-auto">
        {/* Header */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
                   
          {/* Mood Selector */}
          <div className={`p-3 rounded-xl ${currentTheme.card} border`}>
            <p className={`text-sm mb-2 ${currentTheme.textSecondary}`}>How are you feeling today?</p>
            <div className="flex space-x-3">
              {['happy', 'neutral', 'sad'].map((m) => (
                <button
                  key={m}
                  onClick={() => handleMoodSelect(m)}
                  className={`p-2 rounded-full ${mood === m ? 'bg-blue-500/20' : currentTheme.card} transition-all`}
                >
                  {m === 'happy' && <Smile size={20} className={mood === m ? 'text-yellow-400' : currentTheme.textSecondary} />}
                  {m === 'neutral' && <Meh size={20} className={mood === m ? 'text-gray-400' : currentTheme.textSecondary} />}
                  {m === 'sad' && <Frown size={20} className={mood === m ? 'text-blue-400' : currentTheme.textSecondary} />}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Navigation Tabs */}
        <div className={`flex mb-6 rounded-xl ${currentTheme.card} border p-1`}>
          {['today', 'suggestions', 'history'].map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`flex-1 py-2 text-sm font-medium rounded-lg transition-all ${
                activeTab === tab 
                  ? 'bg-blue-500 text-white shadow' 
                  : `${currentTheme.textSecondary} hover:${currentTheme.text}`
              }`}
            >
              {tab.charAt(0).toUpperCase() + tab.slice(1)}
            </button>
          ))}
        </div>

        {/* Today's Journey Tab */}
        {activeTab === 'today' && (
          <div className={`p-6 rounded-3xl ${currentTheme.card} border shadow-xl`}>
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center space-x-3">
                <Compass className={`w-7 h-7 ${currentTheme.accent}`} />
                <h3 className={`text-xl font-light ${currentTheme.text}`}>Today's Routine</h3>
              </div>
              <div className={`px-4 py-2 rounded-full ${currentTheme.card} border`}>
                <span className={`text-sm ${completionPercentage > 70 ? 'text-green-400' : completionPercentage > 40 ? 'text-yellow-400' : 'text-red-400'}`}>
                  {completionPercentage}% Complete
                </span>
              </div>
            </div>
            
            <div className="space-y-4">
              {userRoutines.map((activity) => (
                <div 
                  key={activity.id} 
                  className={`p-4 rounded-2xl ${currentTheme.card} border hover:shadow-md transition-all cursor-pointer ${activity.completed ? 'opacity-80' : ''}`}
                  onClick={() => setExpandedActivity(expandedActivity === activity.id ? null : activity.id)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4">
                      <button 
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleActivityCompletion(activity.id);
                        }}
                        className={`p-1 rounded-full ${activity.completed ? 'text-green-400' : currentTheme.textSecondary}`}
                      >
                        <CheckCircle size={20} className={activity.completed ? 'fill-current' : ''} />
                      </button>
                      <div>
                        <div className={`flex items-center space-x-2 ${currentTheme.text} ${activity.completed ? 'line-through' : ''}`}>
                          {activity.icon}
                          <span>{activity.activity}</span>
                        </div>
                        <div className={`text-xs mt-1 ${currentTheme.textSecondary}`}>
                          <Clock size={14} className="inline mr-1" />
                          {activity.time} • <Flame size={14} className="inline mr-1" />
                          {activity.streak} day streak
                        </div>
                      </div>
                    </div>
                    <ChevronRight className={`w-5 h-5 ${currentTheme.textSecondary} transform ${expandedActivity === activity.id ? 'rotate-90' : ''}`} />
                  </div>
                  
                  {expandedActivity === activity.id && (
                    <div className={`mt-4 pt-4 border-t ${currentTheme.divider}`}>
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div>
                          <div className={`${currentTheme.textSecondary}`}>Suggested Duration</div>
                          <div className={currentTheme.text}>30 minutes</div>
                        </div>
                        <div>
                          <div className={`${currentTheme.textSecondary}`}>Best Time</div>
                          <div className={currentTheme.text}>Morning</div>
                        </div>
                        <div>
                          <div className={`${currentTheme.textSecondary}`}>Category</div>
                          <div className={currentTheme.text}>Fitness</div>
                        </div>
                        <div>
                          <div className={`${currentTheme.textSecondary}`}>Priority</div>
                          <div className={currentTheme.text}>High</div>
                        </div>
                      </div>
                      <div className="mt-4 flex space-x-2">
                        <button className={`px-3 py-1 text-xs rounded-lg ${currentTheme.card} border hover:bg-blue-500/10`}>
                          Reschedule
                        </button>
                        <button className={`px-3 py-1 text-xs rounded-lg ${currentTheme.card} border hover:bg-blue-500/10`}>
                          Add Note
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              ))}
              
              <button className={`w-full p-4 rounded-2xl ${currentTheme.card} border border-dashed flex items-center justify-center space-x-2 ${currentTheme.textSecondary} hover:${currentTheme.text} transition-all`}>
                <Plus size={20} />
                <span>Add New Activity</span>
              </button>
            </div>
          </div>
        )}

        {/* Time-based Suggestions Tab */}
        {activeTab === 'suggestions' && (
          <div className={`p-6 rounded-3xl ${currentTheme.card} border shadow-xl`}>
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center space-x-3">
                <Sun className={`w-7 h-7 ${currentTheme.accent}`} />
                <h3 className={`text-xl font-light ${currentTheme.text}`}>
                  {getTimeOfDay().charAt(0).toUpperCase() + getTimeOfDay().slice(1)} Suggestions
                </h3>
              </div>
              <div className={`px-4 py-2 rounded-full ${currentTheme.card} border`}>
                <span className={`text-sm ${currentTheme.textSecondary}`}>
                  {currentTime.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                </span>
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              {timeBasedSuggestions[getTimeOfDay()].map((activity) => (
                <div key={activity.id} className={`p-4 rounded-2xl ${currentTheme.card} border hover:shadow-md transition-all`}>
                  <div className="flex items-start space-x-3">
                    <div className={`p-2 rounded-lg ${activity.category === 'fitness' ? 'bg-red-500/20 text-red-400' : 
                                      activity.category === 'nutrition' ? 'bg-green-500/20 text-green-400' :
                                      activity.category === 'productivity' ? 'bg-blue-500/20 text-blue-400' :
                                      activity.category === 'learning' ? 'bg-purple-500/20 text-purple-400' :
                                      'bg-yellow-500/20 text-yellow-400'}`}>
                      {activity.icon}
                    </div>
                    <div>
                      <div className={`font-medium ${currentTheme.text}`}>{activity.activity}</div>
                      <div className={`text-xs mt-1 ${currentTheme.textSecondary}`}>
                        <Clock size={14} className="inline mr-1" />
                        {activity.duration} • {activity.category.charAt(0).toUpperCase() + activity.category.slice(1)}
                      </div>
                    </div>
                  </div>
                  <div className="mt-4 flex space-x-2">
                    <button className={`flex-1 py-2 text-xs rounded-lg ${currentTheme.card} border hover:bg-blue-500/10`}>
                      Add to Today
                    </button>
                    <button className={`flex-1 py-2 text-xs rounded-lg ${currentTheme.card} border hover:bg-blue-500/10`}>
                      Schedule
                    </button>
                  </div>
                </div>
              ))}
            </div>
            
            <div className={`p-4 rounded-2xl ${currentTheme.card} border`}>
              <h4 className={`text-sm font-medium mb-3 ${currentTheme.text}`}>Other Times of Day</h4>
              <div className="grid grid-cols-3 gap-2">
                {Object.keys(timeBasedSuggestions).filter(t => t !== getTimeOfDay()).map((time) => (
                  <button
                    key={time}
                    onClick={() => {}}
                    className={`py-2 text-xs rounded-lg ${currentTheme.card} border hover:bg-blue-500/10`}
                  >
                    {time.charAt(0).toUpperCase() + time.slice(1)}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Activity History Tab */}
        {activeTab === 'history' && (
          <div className={`p-6 rounded-3xl ${currentTheme.card} border shadow-xl`}>
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center space-x-3">
                <Calendar className={`w-7 h-7 ${currentTheme.accent}`} />
                <h3 className={`text-xl font-light ${currentTheme.text}`}>Past Activities</h3>
              </div>
              <div className={`px-4 py-2 rounded-full ${currentTheme.card} border`}>
                <span className={`text-sm ${currentTheme.textSecondary}`}>
                  Last 30 Days
                </span>
              </div>
            </div>
            
            <div className="space-y-6">
              {pastActivities.map((day) => (
                <div key={day.date} className={`p-4 rounded-2xl ${currentTheme.card} border`}>
                  <div className="flex items-center justify-between mb-3">
                    <div className={`font-medium ${currentTheme.text}`}>{day.date}</div>
                    <div className={`text-sm ${day.completion === '100%' ? 'text-green-400' : 'text-yellow-400'}`}>
                      {day.completion} completed
                    </div>
                  </div>
                  
                  <div className="space-y-3">
                    {day.activities.map((activity) => (
                      <div key={activity.id} className={`p-3 rounded-lg ${currentTheme.card} border`}>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-3">
                            <div className={`w-3 h-3 rounded-full ${activity.completed ? 'bg-green-400' : 'bg-gray-500'}`} />
                            <div className={`${currentTheme.text} ${activity.completed ? '' : 'opacity-80'}`}>
                              {activity.activity}
                            </div>
                          </div>
                          <div className={`text-xs ${currentTheme.textSecondary}`}>
                            {activity.duration}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
              
              <div className={`p-4 rounded-2xl ${currentTheme.card} border`}>
                <h4 className={`text-sm font-medium mb-3 ${currentTheme.text}`}>Statistics</h4>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className={`p-3 rounded-lg ${currentTheme.card} border`}>
                    <div className={`text-xs ${currentTheme.textSecondary}`}>Best Streak</div>
                    <div className={`text-xl font-medium ${currentTheme.text}`}>18 days</div>
                  </div>
                  <div className={`p-3 rounded-lg ${currentTheme.card} border`}>
                    <div className={`text-xs ${currentTheme.textSecondary}`}>Avg. Completion</div>
                    <div className={`text-xl font-medium ${currentTheme.text}`}>84%</div>
                  </div>
                  <div className={`p-3 rounded-lg ${currentTheme.card} border`}>
                    <div className={`text-xs ${currentTheme.textSecondary}`}>Most Active</div>
                    <div className={`text-xl font-medium ${currentTheme.text}`}>Mornings</div>
                  </div>
                  <div className={`p-3 rounded-lg ${currentTheme.card} border`}>
                    <div className={`text-xs ${currentTheme.textSecondary}`}>Favorite</div>
                    <div className={`text-xl font-medium ${currentTheme.text}`}>Reading</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DailyJourney;